/* eslint-disable indent */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddLedgerEntry from 'components/add-entry-modal/add-entry-modal';
import { createAddEntrySchema } from 'components/add-entry-modal/services/validate-entry';
import { ContainedButton } from 'components/ContainedButton';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountingType } from 'v2-types/order';
import { SplitPayeePayer } from 'views/orders/new-charges/components/split-payee/split-payee';

import Header from '../components/header';
import { columnsDataSet } from '../components/table-charges-kmln/services';
import TablePrototypeKdues from '../components/table-charges-kmln/table-prototype-kdues';
import { chargesSorted, getDisabledFieldsv2 } from '../services';
import { mockData } from './services';

const addEntrySchema = createAddEntrySchema({ requireLetter: true });

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  marginCard: { marginBottom: theme.spacing(2) },
}));

type ChargesKMSCeneProps = {
  accountingData: AccountingType[];
  handleEditModal: (item: columnsDataSet) => void;
  handleDelete: (item: columnsDataSet) => void;
  openAddModal: boolean;
  handleaddModal: (modal: string | null) => void;
  state: { type: string; data?: any };
  showHeaderAndContainer: boolean;
  reloadAccounting: () => void;
};

const ChargesKMScene: FC<ChargesKMSCeneProps> = ({
  accountingData,
  handleEditModal,
  handleDelete,
  openAddModal,
  handleaddModal,
  state,
  showHeaderAndContainer,
  reloadAccounting,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [partySelection, setPartySelection] = useState<{
    kind: 'Payee' | 'Payer';
    charge: AccountingType;
  } | null>(null);
  const [itemsCollapsed, setItemsCollapsed] = useState<{ [key: string]: boolean }>({});
  const mapedData = useMemo(() => mockData(chargesSorted(accountingData, true)), [accountingData]);

  const handleToggleCollapse = (item: columnsDataSet) =>
    setItemsCollapsed((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  const handleChangeParties = (item: columnsDataSet, kind: 'Payee' | 'Payer') =>
    setPartySelection({ kind, charge: item.chargeData });
  const handleChangePayees = (item: columnsDataSet) => handleChangeParties(item, 'Payee');
  const handleChangePayers = (item: columnsDataSet) => handleChangeParties(item, 'Payer');

  const paperContent = (
    <Grid container direction="row">
      <Grid item xs={12} md={6}>
        <Box display="flex" flexDirection="column">
          <TablePrototypeKdues
            data={mapedData.kdues}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:k-due-borrower')}
            type="dues"
          />
          <TablePrototypeKdues
            data={mapedData.kAdjustments}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments')}
            type="adjustments"
          />
          <TablePrototypeKdues
            data={mapedData.kAdjustmentsItems}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items')}
            type="adjustments"
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box display="flex" flexDirection="column">
          <TablePrototypeKdues
            data={mapedData.ldues}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:m-due-seller')}
            type="dues"
          />
          <TablePrototypeKdues
            data={mapedData.ladjustments}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items')}
            type="adjustments"
          />
        </Box>
      </Grid>
    </Grid>
  );

  const tables = (
    <>
      {(mapedData.kaddendums.length > 0 || mapedData.laddendums.length > 0) && (
        <Grid item xs={12} className={classes.marginCard}>
          <Paper>
            <Grid container direction="row">
              <Grid item xs={12} md={6}>
                <TablePrototypeKdues
                  data={mapedData.kaddendums}
                  handleEditModal={handleEditModal}
                  handleDelete={handleDelete}
                  handleChangePayees={handleChangePayees}
                  handleChangePayers={handleChangePayers}
                  itemsCollapsed={itemsCollapsed}
                  onToggleCollapse={handleToggleCollapse}
                  title={t('orders:addendums-k')}
                  type="addendums"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TablePrototypeKdues
                  data={mapedData.laddendums}
                  handleEditModal={handleEditModal}
                  handleDelete={handleDelete}
                  handleChangePayees={handleChangePayees}
                  handleChangePayers={handleChangePayers}
                  itemsCollapsed={itemsCollapsed}
                  onToggleCollapse={handleToggleCollapse}
                  title={t('orders:addendums-m')}
                  type="addendums"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}
      <Grid item xs={12} className={classes.marginCard}>
        <ContainedButton
          icon="plus"
          text={t('charges:addendum')}
          onClick={() => {
            handleaddModal('add');
          }}
        />
      </Grid>
    </>
  );

  const modals = (
    <>
      {openAddModal && (
        <AddLedgerEntry
          open={!!openAddModal}
          handleClose={() => {
            handleaddModal(null);
          }}
          prevData={state.data!}
          kindAllowedEntry={['manual']}
          codeAllowedEntry={['charge']}
          defaultCode="charge"
          defaultKind="manual"
          lettersAllowedEntry={['k', 'm']}
          resolver={zodResolver(addEntrySchema)}
          disableFields={getDisabledFieldsv2(state)}
          hideFields={
            state.type === 'edit'
              ? {
                months: true,
                code: true,
                kind: true,
                months_advanced: true,
                annuality: true,
                entryDate: true,
                amount: true,
              }
              : {
                months: true,
                amount_override: true,
                months_advanced: true,
                annuality: true,
                entryDate: true,
                number: true,
                code: true,
                kind: true,
              }
          }
          isEditingEntry={state.type === 'edit'}
        />
      )}
      {partySelection && (
        <SplitPayeePayer
          open
          kind={partySelection.kind}
          accountingEntry={partySelection.charge}
          onClose={() => setPartySelection(null)}
          onSuccess={() => {
            setPartySelection(null);
            reloadAccounting();
          }}
        />
      )}
    </>
  );
  if (!showHeaderAndContainer) {
    return (
      <>
        <Grid container direction="column">
          {paperContent}
          {tables}
          {modals}
        </Grid>
      </>
    );
  }
  return (
    <>
      <Grid container direction="column" className={classes.padding}>
        <Grid item xs={12}>
          <Paper>
            <Header title={t('charges:debits-and-credits')} chargesIdentifier="K-M" />
            {paperContent}
          </Paper>
        </Grid>
        {tables}
        {modals}
      </Grid>
    </>
  );
};

export default ChargesKMScene;
